import {
  cssSpacing,
  Table as TableComponent,
  TableCell,
  TableContainer,
  TableHeaderCell,
  TableRow,
  TextLink,
  Type,
} from '@spotify-internal/encore-web';
import { Stack } from '@spotify-internal/encore-web/ads-business';
import {
  ExpandableTableRow,
  LoadingSkeletonLine,
} from '@spotify-internal/encore-web/content-platform';
import { useT } from '@spotify-internal/i18n-core';
import { HelpIconWithTooltip } from '../HelpIconWithTooltip';
import type { DataTableHeaderProps, DataTableProps } from './types';

export const DataTable = <T,>({
  headers,
  data,
  expandable,
  onExpandRow,
  hasHover = false,
  isLoading = false,
}: DataTableProps<T>) => {
  const Row = expandable ? ExpandableTableRow : TableRow;
  const t = useT();

  const getRowComponent = (row: T, header: DataTableHeaderProps<T>) => {
    if (!!header.renderRow) {
      return header.renderRow(row);
    }

    if (header.isLink) {
      return (
        <Type variant="viola">
          <TextLink
            href={header.href(row)}
            standalone
            semanticColor="textBrightAccent"
          >
            {row[header.property]}
          </TextLink>
        </Type>
      );
    }

    return (
      <Type variant="viola" semanticColor="textSubdued">
        {row[header.property]}
      </Type>
    );
  };

  const getHeaderComponent = (
    header: DataTableHeaderProps<T>,
    index: number,
  ) => {
    const {
      property,
      helperText,
      label,
      sortedByDirection,
      onClickSort,
      isSortable,
    } = header;

    const content = !helperText ? (
      label
    ) : (
      <Stack gap={cssSpacing('tighter-5', '4px')} alignItems="center" direction="horizontal">
        {label}
        <HelpIconWithTooltip text={helperText} size={16} />
      </Stack>
    );

    if (isSortable) {
      return (
        <TableHeaderCell
          key={`header-cell-${String(property)}-${index}`}
          sortable
          sortedByDirection={sortedByDirection}
          iconPlacement="leading"
          onClick={() => {
            const newSortByDirection =
              sortedByDirection === 'ascending' ? 'descending' : 'ascending';
            onClickSort(newSortByDirection, sortedByDirection);
          }}
        >
          {content}
        </TableHeaderCell>
      );
    }

    return (
      <TableHeaderCell key={`header-cell-${String(property)}-${index}`}>
        {content}
      </TableHeaderCell>
    );
  };

  const getExpandableContent = (row: T) => {
    if (!!onExpandRow) {
      return onExpandRow(row);
    }
    return undefined;
  };

  return (
    <>
      <TableContainer>
        <TableComponent>
          <colgroup>
            {expandable && <col width="5%" />}
            {headers.map(({ width, property }, index) => (
              <col width={width} key={`col-${String(property)}-${index}`} />
            ))}
          </colgroup>
          <thead>
            <TableRow>
              {expandable && <td>{/** left blank intentionally */}</td>}
              {headers.map((headerProps, headerIndex) =>
                getHeaderComponent(headerProps, headerIndex),
              )}
            </TableRow>
          </thead>
          <tbody>
            {data.map((item, itemIndex) => {
              const expandableContent = getExpandableContent(item);
              return (
                <Row
                  hover={hasHover}
                  key={`table-row-${itemIndex}`}
                  expandableContent={expandableContent}
                  expandLabel={t(
                    'atlas.actions.expand_view',
                    'Expand to view',
                    'Text button to expand row',
                  )}
                >
                  {headers.map((header, headerIndex) => (
                    <TableCell
                      key={`table-cell${String(
                        header.property,
                      )}-${headerIndex}`}
                    >
                      {isLoading ? (
                        <LoadingSkeletonLine
                          data-testid={`table-cell${String(
                            header.property,
                          )}-${headerIndex}-loading`}
                        />
                      ) : (
                        getRowComponent(item, header)
                      )}
                    </TableCell>
                  ))}
                </Row>
              );
            })}
          </tbody>
        </TableComponent>
      </TableContainer>
    </>
  );
};
